// manufacturing/RawMaterialPurchase.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Select from 'react-select';
import {
    Plus, Search, RotateCcw, Eye, Edit, Trash2, Printer, ChevronUp,
    ShoppingCart, Calendar, Package, DollarSign, Truck,
} from 'feather-icons-react/build/IconComponents';
import { rawMaterialAPI, supplierAPI } from "../../core/action/api";
import { manufacturingActions } from "../../core/action/manufacturingAction";
import ImageWithBasePath from '../../core/img/imagewithbasebath';

const MySwal = withReactContent(Swal);

const RawMaterialPurchase = () => {
    const dispatch = useDispatch();
    const reduxData = useSelector((state) => state.toggle_header);

    // State management
    const [purchases, setPurchases] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Form state
    const emptyForm = {
        purchase_date: new Date().toISOString().split('T')[0],
        supplier_id: '',
        invoice_no: '',
        reference_no: '',
        notes: '',
        items: []
    };
    const [formData, setFormData] = useState(emptyForm);
    const [errors, setErrors] = useState({});

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        supplier_id: '',
        from_date: '',
        to_date: ''
    });

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    const canCreate = manufacturingActions.hasManufacturingPermission('create-raw-material-purchase');

    // ─────────────────────────────────────────────────────────────
    // Load data
    // ─────────────────────────────────────────────────────────────
    const loadPurchases = async (page = pagination.page, overrideFilters = null) => {
        setLoading(true);
        const activeFilters = overrideFilters !== null ? overrideFilters : filters;
        try {
            const params = {
                page,
                limit: pagination.limit,
                search: activeFilters.search,
                supplier_id: activeFilters.supplier_id,
                from_date: activeFilters.from_date,
                to_date: activeFilters.to_date
            };
            const response = await rawMaterialAPI.getPurchases(params);
            if (response?.success) {
                setPurchases(response.data || []);
                setPagination({
                    page: response.page || page,
                    limit: pagination.limit,
                    total: response.total || 0,
                    totalPages: response.total_pages || 1
                });
            } else {
                toast.error(response?.message || 'Failed to load purchases');
            }
        } catch (error) {
            console.error('Error loading purchases:', error);
            toast.error('Failed to load purchases');
        } finally {
            setLoading(false);
        }
    };

    const loadSuppliers = async () => {
        try {
            const response = await supplierAPI.getAll({ limit: 1000 });
            if (response?.success && response?.data) setSuppliers(response.data);
            else setSuppliers([]);
        } catch (error) {
            console.error('Error loading suppliers:', error);
            setSuppliers([]);
        }
    };

    const loadRawMaterials = async () => {
        try {
            const response = await rawMaterialAPI.getAll({ limit: 1000, status: 'active' });
            if (response?.success && response?.data) setRawMaterials(response.data);
            else setRawMaterials([]);
        } catch (error) {
            console.error('Error loading raw materials:', error);
            setRawMaterials([]);
        }
    };

    useEffect(() => {
        loadPurchases();
        loadSuppliers();
        loadRawMaterials();
    }, []);

    // ─────────────────────────────────────────────────────────────
    // Filter handlers
    // ─────────────────────────────────────────────────────────────
    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        loadPurchases(1, newFilters);
    };

    // ─────────────────────────────────────────────────────────────
    // Form handlers
    // ─────────────────────────────────────────────────────────────
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSelectChange = (selected, name) => {
        setFormData(prev => ({ ...prev, [name]: selected?.value || '' }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleAddMaterial = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                id: Date.now(),
                material_id: '',
                material_name: '',
                sku: '',
                quantity: 0,
                unit: 'PCS',
                unit_price: '',
                total_price: 0,
                remarks: ''
            }]
        }));
    };

    const handleRemoveMaterial = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handleMaterialSelect = (index, selectedOption) => {
        if (!selectedOption) return;
        const material = selectedOption.data;
        const updatedItems = [...formData.items];
        updatedItems[index] = {
            ...updatedItems[index],
            material_id: material.id,
            material_name: material.material_name,
            sku: material.sku,
            quantity: 0,
            unit: material.unit_name || 'PCS',
            unit_price: 0,
            total_price: 0,
            remarks: ''
        };
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    const handleQuantityChange = (index, quantity) => {
        const updatedItems = [...formData.items];
        const qty = parseFloat(quantity) || 0;
        updatedItems[index].quantity = qty;
        updatedItems[index].total_price = (parseFloat(updatedItems[index].unit_price) || 0) * qty;
        setFormData(prev => ({ ...prev, items: updatedItems }));
        if (errors[`quantity_${index}`]) setErrors(prev => ({ ...prev, [`quantity_${index}`]: '' }));
    };

    const handleUnitPriceChange = (index, price) => {
        const updatedItems = [...formData.items];
        updatedItems[index].unit_price = price; // keep raw string so user can type e.g. "0.0987"
        const parsed = parseFloat(price) || 0;
        updatedItems[index].total_price = parsed * (updatedItems[index].quantity || 0);
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    const isSelfManufacturing = () => {
        const selected = suppliers.find(s => s.id === formData.supplier_id);
        return selected && selected.name === 'Self Manufacturing';
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.supplier_id) newErrors.supplier_id = 'Supplier is required';
        if (formData.items.length === 0) newErrors.items = 'At least one raw material is required';
        let hasValid = false;
        formData.items.forEach((item, i) => {
            if (!item.material_id) newErrors[`material_${i}`] = 'Select raw material';
            if (item.quantity <= 0) newErrors[`quantity_${i}`] = 'Quantity > 0 required';
            if (item.material_id && item.quantity > 0 && (!isSelfManufacturing() && parseFloat(item.unit_price) <= 0))
                newErrors[`price_${i}`] = 'Unit price > 0 required';
            if (item.material_id && item.quantity > 0) hasValid = true;
        });
        if (!hasValid && formData.items.length > 0) newErrors.items = 'At least one valid item with quantity > 0';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const resetForm = () => {
        setFormData(emptyForm);
        setErrors({});
    };

    // ─────────────────────────────────────────────────────────────
    // Create
    // ─────────────────────────────────────────────────────────────
    const handleCreate = async () => {
        if (!validateForm()) {
            toast.error('Please fix form errors');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                purchase_date: formData.purchase_date,
                supplier_id: formData.supplier_id,
                invoice_no: formData.invoice_no || null,
                reference_no: formData.reference_no || null,
                notes: formData.notes || null,
                items: formData.items
                    .filter(i => i.material_id && i.quantity > 0)
                    .map(i => ({
                        material_id: i.material_id,
                        quantity: i.quantity,
                        unit: i.unit,
                        unit_price: parseFloat(i.unit_price) || 0,
                        remarks: i.remarks || null
                    }))
            };
            const result = await rawMaterialAPI.createPurchase(payload);
            if (result?.success) {
                toast.success(result.message || 'Purchase created');
                resetForm();
                setShowCreateModal(false);
                await loadPurchases(1);
                await loadRawMaterials();
            } else {
                toast.error(result?.message || 'Create failed');
            }
        } catch (error) {
            console.error(error);
            toast.error('Create failed');
        } finally {
            setSubmitting(false);
        }
    };

    // ─────────────────────────────────────────────────────────────
    // Edit
    // ─────────────────────────────────────────────────────────────
    const handleEditPurchase = async (purchase) => {
        try {
            setViewLoading(true);
            const response = await rawMaterialAPI.getPurchase(purchase.id);
            if (response?.success && response?.data) {
                const d = response.data;
                setSelectedPurchase(d);
                setFormData({
                    purchase_date: d.purchase_date,
                    supplier_id: d.supplier_id,
                    invoice_no: d.invoice_no || '',
                    reference_no: d.reference_no || '',
                    notes: d.notes || '',
                    items: (d.items || []).map(item => ({
                        id: item.id || Date.now(),
                        material_id: item.material_id,
                        material_name: item.material_name,
                        sku: item.sku,
                        quantity: parseFloat(item.quantity),
                        unit: item.unit || 'PCS',
                        unit_price: String(parseFloat(item.unit_price) || 0),
                        total_price: parseFloat(item.total_price),
                        remarks: item.remarks || ''
                    }))
                });
                setShowEditModal(true);
            } else {
                toast.error(response?.message || 'Failed to load purchase');
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to load purchase details');
        } finally {
            setViewLoading(false);
        }
    };

    const handleUpdate = async () => {
        if (!validateForm()) {
            toast.error('Please fix form errors');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                id: selectedPurchase.id,
                purchase_date: formData.purchase_date,
                supplier_id: formData.supplier_id,
                invoice_no: formData.invoice_no || null,
                reference_no: formData.reference_no || null,
                notes: formData.notes || null,
                items: formData.items
                    .filter(i => i.material_id && i.quantity > 0)
                    .map(i => ({
                        material_id: i.material_id,
                        quantity: i.quantity,
                        unit: i.unit,
                        unit_price: parseFloat(i.unit_price) || 0,
                        remarks: i.remarks || null
                    }))
            };
            const result = await rawMaterialAPI.updatePurchase(selectedPurchase.id, payload);
            if (result?.success) {
                toast.success(result.message || 'Purchase updated');
                setShowEditModal(false);
                resetForm();
                setSelectedPurchase(null);
                await loadPurchases(pagination.page);
                await loadRawMaterials();
            } else {
                toast.error(result?.message || 'Update failed');
            }
        } catch (error) {
            console.error(error);
            toast.error('Update failed');
        } finally {
            setSubmitting(false);
        }
    };

    // ─────────────────────────────────────────────────────────────
    // Delete
    // ─────────────────────────────────────────────────────────────
    const handleDeletePurchase = async (purchase) => {
        const confirm = await MySwal.fire({
            title: 'Delete Purchase?',
            html: `<p>Delete <strong>${purchase.po_no}</strong> ? This will remove all items.</p><p class="text-danger">Cannot be undone.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel'
        });
        if (!confirm.isConfirmed) return;

        try {
            const result = await rawMaterialAPI.deletePurchase(purchase.id);
            if (result?.success) {
                toast.success('Purchase deleted');
                await loadPurchases(pagination.page);
                await loadRawMaterials();
            } else {
                toast.error(result?.message || 'Delete failed');
            }
        } catch (error) {
            console.error(error);
            toast.error('Delete failed');
        }
    };

    // ─────────────────────────────────────────────────────────────
    // View
    // ─────────────────────────────────────────────────────────────
    const handleViewPurchase = async (purchase) => {
        setViewLoading(true);
        setShowViewModal(true);
        try {
            const response = await rawMaterialAPI.getPurchase(purchase.id);
            if (response?.success && response?.data) {
                setSelectedPurchase(response.data);
            } else {
                toast.error(response?.message || 'Failed to load details');
                setShowViewModal(false);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to load details');
            setShowViewModal(false);
        } finally {
            setViewLoading(false);
        }
    };

    // ─────────────────────────────────────────────────────────────
    // Helpers
    // ─────────────────────────────────────────────────────────────
    const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-IN') : 'N/A';
    const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amt || 0);

    const supplierOptions = suppliers.map(s => ({ value: s.id, label: `${s.name}${s.gst_no ? ` (${s.gst_no})` : ''}`, data: s }));
    const rawMaterialOptions = rawMaterials.map(m => ({ value: m.id, label: `${m.material_name} (${m.sku}) - Stock: ${m.quantity || 0} ${m.unit_name || 'PCS'}`, data: m }));

    const totals = {
        totalItems: formData.items.length,
        totalQuantity: formData.items.reduce((s, i) => s + (i.quantity || 0), 0),
        totalValue: formData.items.reduce((s, i) => s + (i.total_price || 0), 0)
    };

    const renderTooltip = (props, text) => <Tooltip id={`tt-${text}`} {...props}>{text}</Tooltip>;

    // ─────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────
    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Raw Material Purchase</h4>
                        <h6>Purchase raw materials from suppliers</h6>
                    </div>
                    <ul className="table-top-head">
                        <li><OverlayTrigger placement="top" overlay={p => renderTooltip(p, 'PDF')}><Link to="#"><ImageWithBasePath src="assets/img/icons/pdf.svg" alt="PDF" /></Link></OverlayTrigger></li>
                        <li><OverlayTrigger placement="top" overlay={p => renderTooltip(p, 'Excel')}><Link to="#"><ImageWithBasePath src="assets/img/icons/excel.svg" alt="Excel" /></Link></OverlayTrigger></li>
                        <li><OverlayTrigger placement="top" overlay={p => renderTooltip(p, 'Print')}><Link onClick={() => window.print()}><Printer /></Link></OverlayTrigger></li>
                        <li><OverlayTrigger placement="top" overlay={p => renderTooltip(p, 'Refresh')}><Link onClick={() => { loadPurchases(); loadRawMaterials(); }}><RotateCcw /></Link></OverlayTrigger></li>
                        <li><OverlayTrigger placement="top" overlay={p => renderTooltip(p, 'Collapse')}><Link className={reduxData ? 'active' : ''} onClick={() => dispatch(setToogleHeader(!reduxData))}><ChevronUp /></Link></OverlayTrigger></li>
                    </ul>
                    {canCreate && (
                        <div className="page-btn">
                            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                                <Plus size={18} className="me-2" /> Purchase Raw Material
                            </button>
                        </div>
                    )}
                </div>

                {/* Stat Cards */}
                <div className="row">
                    <div className="col-lg-3 col-md-6"><div className="card"><div className="card-body"><div className="d-flex align-items-center"><div className="bg-primary bg-opacity-10 p-3 rounded me-3"><ShoppingCart size={24} className="text-primary" /></div><div><p className="text-muted mb-1">Total Purchases</p><h3 className="mb-0">{pagination.total}</h3></div></div></div></div></div>
                    <div className="col-lg-3 col-md-6"><div className="card"><div className="card-body"><div className="d-flex align-items-center"><div className="bg-success bg-opacity-10 p-3 rounded me-3"><Package size={24} className="text-success" /></div><div><p className="text-muted mb-1">Total Items</p><h3 className="mb-0">{purchases.reduce((s, p) => s + (p.total_items || 0), 0)}</h3></div></div></div></div></div>
                    <div className="col-lg-3 col-md-6"><div className="card"><div className="card-body"><div className="d-flex align-items-center"><div className="bg-warning bg-opacity-10 p-3 rounded me-3"><DollarSign size={24} className="text-warning" /></div><div><p className="text-muted mb-1">Total Value</p><h3 className="mb-0">{formatCurrency(purchases.reduce((s, p) => s + (parseFloat(p.total_value) || 0), 0))}</h3></div></div></div></div></div>
                    <div className="col-lg-3 col-md-6"><div className="card"><div className="card-body"><div className="d-flex align-items-center"><div className="bg-info bg-opacity-10 p-3 rounded me-3"><Truck size={24} className="text-info" /></div><div><p className="text-muted mb-1">Suppliers</p><h3 className="mb-0">{new Set(purchases.map(p => p.supplier_id)).size}</h3></div></div></div></div></div>
                </div>

                {/* Filters */}
                <div className="card mb-4"><div className="card-body"><div className="row">
                    <div className="col-lg-4"><label>Search</label><div className="input-group"><input type="text" className="form-control" placeholder="PO No, Invoice No..." value={filters.search} onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))} onKeyPress={e => { if (e.key === 'Enter') { const f = { ...filters, search: e.target.value }; setFilters(f); loadPurchases(1, f); } }} /><button className="btn btn-primary" onClick={() => loadPurchases(1, filters)}><Search size={16} /></button></div></div>
                    <div className="col-lg-3"><label>Supplier</label><Select options={supplierOptions} isClearable placeholder="All Suppliers" onChange={selected => handleFilterChange('supplier_id', selected?.value || '')} /></div>
                    <div className="col-lg-2"><label>From Date</label><input type="date" className="form-control" value={filters.from_date} onChange={e => handleFilterChange('from_date', e.target.value)} /></div>
                    <div className="col-lg-2"><label>To Date</label><input type="date" className="form-control" value={filters.to_date} onChange={e => handleFilterChange('to_date', e.target.value)} /></div>
                    <div className="col-lg-1"><label>&nbsp;</label><button className="btn btn-secondary w-100" onClick={() => { const emptyFilters = { search: '', supplier_id: '', from_date: '', to_date: '' }; setFilters(emptyFilters); loadPurchases(1, emptyFilters); }}>Clear</button></div>
                </div></div></div>

                {/* Purchases Table */}
                <div className="card"><div className="card-body">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
                    ) : purchases.length === 0 ? (
                        <div className="text-center py-5"><ShoppingCart size={48} className="text-muted mb-3" /><h5>No purchases found</h5>{canCreate && <button className="btn btn-primary mt-2" onClick={() => setShowCreateModal(true)}><Plus size={16} className="me-2" />Purchase Raw Material</button>}</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th width="40px"><input type="checkbox" className="form-check-input" checked={selectedRows.length === purchases.length && purchases.length > 0} onChange={e => setSelectedRows(e.target.checked ? purchases.map(p => p.id) : [])} /></th>
                                        <th>PO No</th><th>Date</th><th>Supplier</th><th>Invoice No</th><th>Items</th><th>Total Qty</th><th>Total Value</th>
                                        <th className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {purchases.map(p => (
                                        <tr key={p.id}>
                                            <td><input type="checkbox" className="form-check-input" checked={selectedRows.includes(p.id)} onChange={e => setSelectedRows(e.target.checked ? [...selectedRows, p.id] : selectedRows.filter(id => id !== p.id))} /></td>
                                            <td><strong>{p.po_no}</strong>{p.reference_no && <div className="small text-muted">Ref: {p.reference_no}</div>}</td>
                                            <td><Calendar size={14} className="me-1 text-muted" />{formatDate(p.purchase_date)}</td>
                                            <td>{p.supplier_name}</td>
                                            <td>{p.invoice_no ? <code>{p.invoice_no}</code> : <span className="text-muted">-</span>}</td>
                                            <td>{p.total_items || 0}</td>
                                            <td>{p.total_quantity || 0}</td>
                                            <td className="fw-bold text-success">{formatCurrency(p.total_value)}</td>
                                            <td className="text-center">
                                                <button className="btn btn-sm btn-outline-info me-1" onClick={() => handleViewPurchase(p)} title="View"><Eye size={14} /></button>
                                                <button className="btn btn-sm btn-outline-warning me-1" onClick={() => handleEditPurchase(p)} title="Edit"><Edit size={14} /></button>
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeletePurchase(p)} title="Delete"><Trash2 size={14} /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {pagination.totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="text-muted">Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}</div>
                            <nav><ul className="pagination">
                                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}><button className="page-link" onClick={() => loadPurchases(pagination.page - 1)}>Previous</button></li>
                                {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                    let pg = pagination.page;
                                    if (pagination.totalPages <= 5) pg = i + 1;
                                    else if (pagination.page <= 3) pg = i + 1;
                                    else if (pagination.page >= pagination.totalPages - 2) pg = pagination.totalPages - 4 + i;
                                    else pg = pagination.page - 2 + i;
                                    return <li key={pg} className={`page-item ${pagination.page === pg ? 'active' : ''}`}><button className="page-link" onClick={() => loadPurchases(pg)}>{pg}</button></li>;
                                })}
                                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}><button className="page-link" onClick={() => loadPurchases(pagination.page + 1)}>Next</button></li>
                            </ul></nav>
                        </div>
                    )}
                </div></div>
            </div>

            {/* ───────────────────────────────────────────────────────────── */}
            {/* CREATE MODAL */}
            {/* ───────────────────────────────────────────────────────────── */}
            {showCreateModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-dialog-centered modal-xl">
                        <div className="modal-content">
                            <div className="modal-header"><h5 className="modal-title"><ShoppingCart size={18} className="me-2" />Purchase Raw Materials</h5><button type="button" className="btn-close" onClick={() => { setShowCreateModal(false); resetForm(); }} /></div>
                            <div className="modal-body">
                                {errors.form && <div className="alert alert-danger">{errors.form}<button type="button" className="btn-close" onClick={() => setErrors({})} /></div>}
                                <div className="row mb-4">
                                    <div className="col-md-4"><label>Purchase Date *</label><input type="date" name="purchase_date" className="form-control" value={formData.purchase_date} onChange={handleInputChange} /></div>
                                    <div className="col-md-4"><label>Supplier *</label><Select options={supplierOptions} onChange={sel => handleSelectChange(sel, 'supplier_id')} placeholder="Select supplier" className={errors.supplier_id ? 'is-invalid' : ''} />{errors.supplier_id && <div className="text-danger small mt-1">{errors.supplier_id}</div>}</div>
                                    <div className="col-md-4"><label>Invoice No</label><input type="text" name="invoice_no" className="form-control" value={formData.invoice_no} onChange={handleInputChange} placeholder="Supplier invoice number" /></div>
                                    <div className="col-md-6 mt-3"><label>Reference No (PO No)</label><input type="text" name="reference_no" className="form-control" value={formData.reference_no} onChange={handleInputChange} placeholder="Your purchase order number" /></div>
                                    <div className="col-md-6 mt-3"><label>Notes</label><textarea name="notes" className="form-control" rows="2" value={formData.notes} onChange={handleInputChange} placeholder="Additional notes" /></div>
                                </div>

                                <div className="border-top pt-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3"><h6 className="fw-bold"><Package size={16} className="me-2" />Raw Materials to Purchase</h6><button type="button" className="btn btn-sm btn-primary" onClick={handleAddMaterial}><Plus size={14} className="me-1" /> Add Raw Material</button></div>
                                    {errors.items && <div className="alert alert-danger">{errors.items}</div>}
                                    <div className="table-responsive">
                                        <table className="table table-bordered">
                                            <thead className="table-light"><tr><th>Raw Material</th><th>Qty</th><th>Unit</th><th>Unit Price (₹)</th><th>Total (₹)</th><th>Remarks</th><th>Action</th></tr></thead>
                                            <tbody>
                                                {formData.items.length === 0 ? <tr><td colSpan="7" className="text-center py-4 text-muted">Click Add Raw Material</td></tr> :
                                                    formData.items.map((item, idx) => {
                                                        const isSelf = isSelfManufacturing();
                                                        return (
                                                            <tr key={item.id}>
                                                                <td><Select options={rawMaterialOptions} onChange={sel => handleMaterialSelect(idx, sel)} placeholder="Select raw material" className={errors[`material_${idx}`] ? 'is-invalid' : ''} menuPortalTarget={document.body} menuPosition="fixed" styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }} />{errors[`material_${idx}`] && <div className="text-danger small mt-1">{errors[`material_${idx}`]}</div>}</td>
                                                                <td><input type="number" className={`form-control ${errors[`quantity_${idx}`] ? 'is-invalid' : ''}`} value={item.quantity || ''} onChange={e => handleQuantityChange(idx, e.target.value)} step="any" min="0" placeholder="Qty" />{errors[`quantity_${idx}`] && <div className="text-danger small mt-1">{errors[`quantity_${idx}`]}</div>}</td>
                                                                <td><input type="text" className="form-control" value={item.unit} readOnly /></td>
                                                                <td><input type="number" className={`form-control ${errors[`price_${idx}`] ? 'is-invalid' : ''}`} value={item.unit_price || ''} onChange={e => handleUnitPriceChange(idx, e.target.value)} min="0" step="any" placeholder="Price" disabled={isSelf} />{errors[`price_${idx}`] && <div className="text-danger small mt-1">{errors[`price_${idx}`]}</div>}</td>
                                                                <td><input type="text" className="form-control bg-light" value={item.total_price.toFixed(2)} readOnly /></td>
                                                                <td><input type="text" className="form-control" value={item.remarks || ''} onChange={e => { const items = [...formData.items]; items[idx].remarks = e.target.value; setFormData(prev => ({ ...prev, items })); }} placeholder="Remarks" /></td>
                                                                <td className="text-center"><button type="button" className="btn btn-sm btn-danger" onClick={() => handleRemoveMaterial(idx)}><Trash2 size={14} /></button></td>
                                                            </tr>
                                                        );
                                                    })}
                                            </tbody>
                                            {formData.items.length > 0 && <tfoot><tr><td colSpan="2" className="text-end fw-bold">Totals:</td><td className="fw-bold">{totals.totalItems} Items</td><td className="fw-bold">{totals.totalQuantity} Units</td><td className="fw-bold">₹{totals.totalValue.toFixed(2)}</td><td colSpan="2"></td></tr></tfoot>}
                                        </table>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>Cancel</button>
                                <button className="btn btn-primary" onClick={handleCreate} disabled={submitting}>{submitting ? <><span className="spinner-border spinner-border-sm me-2" />Processing...</> : <><ShoppingCart size={16} className="me-2" />Purchase Raw Materials</>}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ───────────────────────────────────────────────────────────── */}
            {/* EDIT MODAL */}
            {/* ───────────────────────────────────────────────────────────── */}
            {showEditModal && selectedPurchase && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-dialog-centered modal-xl">
                        <div className="modal-content">
                            <div className="modal-header"><h5 className="modal-title"><Edit size={18} className="me-2" />Edit Purchase - {selectedPurchase.po_no}</h5><button type="button" className="btn-close" onClick={() => { setShowEditModal(false); resetForm(); setSelectedPurchase(null); }} /></div>
                            <div className="modal-body">
                                {errors.form && <div className="alert alert-danger">{errors.form}</div>}
                                <div className="row mb-4">
                                    <div className="col-md-4"><label>Purchase Date *</label><input type="date" name="purchase_date" className="form-control" value={formData.purchase_date} onChange={handleInputChange} /></div>
                                    <div className="col-md-4"><label>Supplier *</label><Select options={supplierOptions} value={supplierOptions.find(o => o.value === formData.supplier_id)} onChange={sel => handleSelectChange(sel, 'supplier_id')} placeholder="Select supplier" className={errors.supplier_id ? 'is-invalid' : ''} />{errors.supplier_id && <div className="text-danger small mt-1">{errors.supplier_id}</div>}</div>
                                    <div className="col-md-4"><label>Invoice No</label><input type="text" name="invoice_no" className="form-control" value={formData.invoice_no} onChange={handleInputChange} placeholder="Supplier invoice number" /></div>
                                    <div className="col-md-6 mt-3"><label>Reference No (PO No)</label><input type="text" name="reference_no" className="form-control" value={formData.reference_no} onChange={handleInputChange} placeholder="Your purchase order number" /></div>
                                    <div className="col-md-6 mt-3"><label>Notes</label><textarea name="notes" className="form-control" rows="2" value={formData.notes} onChange={handleInputChange} placeholder="Additional notes" /></div>
                                </div>

                                <div className="border-top pt-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3"><h6 className="fw-bold"><Package size={16} className="me-2" />Raw Materials to Purchase</h6><button type="button" className="btn btn-sm btn-primary" onClick={handleAddMaterial}><Plus size={14} className="me-1" /> Add Raw Material</button></div>
                                    {errors.items && <div className="alert alert-danger">{errors.items}</div>}
                                    <div className="table-responsive">
                                        <table className="table table-bordered">
                                            <thead className="table-light"><tr><th>Raw Material</th><th>Qty</th><th>Unit</th><th>Unit Price (₹)</th><th>Total (₹)</th><th>Remarks</th><th>Action</th></tr></thead>
                                            <tbody>
                                                {formData.items.length === 0 ? <tr><td colSpan="7" className="text-center py-4 text-muted">Click Add Raw Material</td></tr> :
                                                    formData.items.map((item, idx) => {
                                                        const isSelf = isSelfManufacturing();
                                                        return (
                                                            <tr key={item.id}>
                                                                <td><Select options={rawMaterialOptions} value={rawMaterialOptions.find(o => o.value === item.material_id)} onChange={sel => handleMaterialSelect(idx, sel)} placeholder="Select raw material" className={errors[`material_${idx}`] ? 'is-invalid' : ''} menuPortalTarget={document.body} menuPosition="fixed" styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }} />{errors[`material_${idx}`] && <div className="text-danger small mt-1">{errors[`material_${idx}`]}</div>}</td>
                                                                <td><input type="number" className={`form-control ${errors[`quantity_${idx}`] ? 'is-invalid' : ''}`} value={item.quantity || ''} onChange={e => handleQuantityChange(idx, e.target.value)} step="any" min="0" placeholder="Qty" />{errors[`quantity_${idx}`] && <div className="text-danger small mt-1">{errors[`quantity_${idx}`]}</div>}</td>
                                                                <td><input type="text" className="form-control" value={item.unit} readOnly /></td>
                                                                <td><input type="number" className={`form-control ${errors[`price_${idx}`] ? 'is-invalid' : ''}`} value={item.unit_price || ''} onChange={e => handleUnitPriceChange(idx, e.target.value)} min="0" step="any" placeholder="Price" disabled={isSelf} />{errors[`price_${idx}`] && <div className="text-danger small mt-1">{errors[`price_${idx}`]}</div>}</td>
                                                                <td><input type="text" className="form-control bg-light" value={item.total_price.toFixed(2)} readOnly /></td>
                                                                <td><input type="text" className="form-control" value={item.remarks || ''} onChange={e => { const items = [...formData.items]; items[idx].remarks = e.target.value; setFormData(prev => ({ ...prev, items })); }} placeholder="Remarks" /></td>
                                                                <td className="text-center"><button type="button" className="btn btn-sm btn-danger" onClick={() => handleRemoveMaterial(idx)}><Trash2 size={14} /></button></td>
                                                            </tr>
                                                        );
                                                    })}
                                            </tbody>
                                            {formData.items.length > 0 && <tfoot><tr><td colSpan="2" className="text-end fw-bold">Totals:</td><td className="fw-bold">{totals.totalItems} Items</td><td className="fw-bold">{totals.totalQuantity} Units</td><td className="fw-bold">₹{totals.totalValue.toFixed(2)}</td><td colSpan="2"></td></tr></tfoot>}
                                        </table>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => { setShowEditModal(false); resetForm(); setSelectedPurchase(null); }}>Cancel</button>
                                <button className="btn btn-primary" onClick={handleUpdate} disabled={submitting}>{submitting ? <><span className="spinner-border spinner-border-sm me-2" />Updating...</> : <><ShoppingCart size={16} className="me-2" />Update Purchase</>}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ───────────────────────────────────────────────────────────── */}
            {/* VIEW MODAL */}
            {/* ───────────────────────────────────────────────────────────── */}
            {showViewModal && selectedPurchase && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header"><h5 className="modal-title"><ShoppingCart size={18} className="me-2" />Purchase Details - {selectedPurchase.po_no}</h5><button type="button" className="btn-close" onClick={() => setShowViewModal(false)} /></div>
                            <div className="modal-body">
                                {viewLoading ? <div className="text-center py-5"><div className="spinner-border" /></div> : (
                                    <>
                                        <div className="row mb-4">
                                            <div className="col-md-6"><div className="card bg-light"><div className="card-body"><h6>Purchase Info</h6><p><strong>PO No:</strong> {selectedPurchase.po_no}</p><p><strong>Date:</strong> {formatDate(selectedPurchase.purchase_date)}</p><p><strong>Supplier:</strong> {selectedPurchase.supplier_name}</p>{selectedPurchase.invoice_no && <p><strong>Invoice No:</strong> {selectedPurchase.invoice_no}</p>}{selectedPurchase.reference_no && <p><strong>Reference:</strong> {selectedPurchase.reference_no}</p>}{selectedPurchase.notes && <p><strong>Notes:</strong> {selectedPurchase.notes}</p>}</div></div></div>
                                            <div className="col-md-6"><div className="card bg-light"><div className="card-body"><h6>Summary</h6><p><strong>Items:</strong> {selectedPurchase.total_items || 0}</p><p><strong>Total Qty:</strong> {selectedPurchase.total_quantity || 0}</p><p><strong>Total Value:</strong> {formatCurrency(selectedPurchase.total_value)}</p></div></div></div>
                                        </div>
                                        <h6 className="fw-bold mb-3">Raw Materials</h6>
                                        <div className="table-responsive"><table className="table table-bordered"><thead className="table-light"><tr><th>Material</th><th>SKU</th><th>Qty</th><th>Unit</th><th>Unit Price</th><th>Total</th><th>Remarks</th></tr></thead><tbody>
                                            {selectedPurchase.items?.length ? selectedPurchase.items.map((it, i) => (
                                                <tr key={i}><td><strong>{it.material_name}</strong></td><td><code>{it.sku}</code></td><td className="text-end">{parseFloat(it.quantity).toLocaleString('en-IN')}</td><td>{it.unit}</td><td className="text-end">{formatCurrency(it.unit_price)}</td><td className="text-end fw-bold">{formatCurrency(it.total_price)}</td><td>{it.remarks || '-'}</td></tr>
                                            )) : <tr><td colSpan="7" className="text-center">No items</td></tr>}
                                        </tbody><tfoot><tr><td colSpan="5" className="text-end fw-bold">Grand Total:</td><td className="fw-bold text-success">{formatCurrency(selectedPurchase.total_value)}</td><td></td></tr></tfoot></table></div>
                                    </>
                                )}
                            </div>
                            <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setShowViewModal(false)}>Close</button></div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RawMaterialPurchase;